import { useEffect, useRef, useState, type ReactNode } from "react"
import { Link } from "@tanstack/react-router"
import { Check, CircleAlert, X } from "lucide-react"
import type { PimWorkflowScope, PimWorkflowDetail, NutritionQueueReceipt } from "@nova-ai/shared"
import type { useNutritionQueue } from "../../services/use-nutrition-queue"
import type { NutritionRecoverySession } from "../../services/use-nutrition-recovery-verification"
import { formatDateTime } from "../../lib/format-date"
import { humanizeEnumLabel } from "../../lib/enum-label"
import { Modal } from "../ui/modal"
import { Button } from "../ui/button"
import { StatusBadge } from "../ui/status-badge"
import { WorkflowStage } from "../pim-workflow-history/workflow-history-evidence"
import {
  workflowReason,
  workflowCoverage,
  scopeFailures,
  WORKFLOW_STAGES,
} from "../pim-workflow-history/workflow-history-presentation"
import { NutritionBlockers } from "./nutrition-blockers"
import { NutritionHelp } from "./nutrition-help"
import { cn } from "../../lib/utils"
import { NutritionRecovery } from "./nutrition-recovery"
import { NutritionImage } from "./nutrition-images"
import { nutritionDuration, nutritionOutcome } from "./nutrition-presentation"

type Props = {
  recoverySession: NutritionRecoverySession
  queue: ReturnType<typeof useNutritionQueue>
  receiptId: number
  selectedStore?: string | undefined
  selectedSku?: string | undefined
  selectedScope?: string | undefined
  failedOnly: boolean
  onSelectScope: (scope: PimWorkflowDetail["blockers"]["scopes"][number]) => void
  onScopeFilter: (failed: boolean) => void
  row?: NutritionQueueReceipt | undefined
  onClose: () => void
}
export function NutritionDetail({
  queue,
  recoverySession,
  receiptId,
  selectedStore,
  selectedSku,
  selectedScope,
  failedOnly,
  onSelectScope,
  onScopeFilter,
  row,
  onClose,
}: Props) {
  const { detail, attempts } = queue
  const first = detail.data?.pages[0]
  const scopes = receiptScopes(queue)
  const recorded = receiptAttempts(queue)
  return (
    <Modal
      label={`Nutrition receipt ${receiptId}`}
      onClose={onClose}
      shellClassName="items-stretch justify-end p-0"
      surfaceClassName="h-dvh max-h-dvh w-full rounded-none sm:max-w-[520px]"
    >
      <NutritionDetailHeading receiptId={receiptId} row={row} first={first} onClose={onClose} />

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-4 sm:px-5 sm:pb-6">
        {detail.isPending && <p role="status">Loading receipt evidence…</p>}

        {detail.error && (
          <p role="alert" className="text-sm text-destructive">
            {first ? "Displayed receipt is stale. " : "Receipt could not be read. "}

            {detail.error.message}
          </p>
        )}

        {detail.error && !first && (
          <div className="flex flex-wrap gap-2">
            <Button
              className="min-h-11"
              onClick={() => {
                void detail.refetch()
              }}
            >
              Retry receipt read
            </Button>

            {failedOnly && (
              <Button className="min-h-11" onClick={() => onScopeFilter(false)}>
                All scopes
              </Button>
            )}
          </div>
        )}

        {queue.selection.error && (
          <p role="alert" className="text-sm text-destructive">
            Selected store evidence could not be read. Use the scope pages below.
          </p>
        )}

        {first && (
          <>
            <ReceiptEvidence detail={first} row={row}>
              <NutritionBlockers
                blockers={first.blockers}
                onSelect={(scope) => {
                  onSelectScope(scope)
                  const existing = document.getElementById(`nutrition-scope-${scope.id}`)
                  existing?.setAttribute("open", "")
                  existing?.querySelector("summary")?.focus()
                  existing?.scrollIntoView({ block: "nearest" })
                }}
                onShowFailed={() => onScopeFilter(true)}
              />
            </ReceiptEvidence>

            {queue.selection.isFetching && (
              <p role="status" className="text-xs">
                Reading selected scope…
              </p>
            )}

            <NutritionScopeList
              detail={detail}
              scopes={scopes}
              total={first.receipt.summary.scopes}
              selectedStore={selectedStore}
              selectedSku={selectedSku}
              selectedScope={selectedScope}
              failedOnly={failedOnly}
              onScopeFilter={onScopeFilter}
              recorded={recorded}
            />

            <NutritionAttempts attempts={attempts} recorded={recorded} />

            <NutritionRecovery scopes={scopes} paused={queue.paused} session={recoverySession} />
          </>
        )}
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-border p-4">
        <CopyReceipt receiptId={receiptId} />

        {first?.receipt.sku && (
          <Link
            to="/products/$sku"
            params={{ sku: first.receipt.sku }}
            className="inline-flex min-h-11 items-center rounded-full px-4 text-sm text-info underline focus-visible:ring-2 focus-visible:ring-ring"
          >
            Open product
          </Link>
        )}
      </footer>
    </Modal>
  )
}

function ReceiptEvidence({
  detail,
  row,
  children,
}: {
  detail: PimWorkflowDetail
  children: ReactNode
  row?: NutritionQueueReceipt | undefined
}) {
  const receipt = detail.receipt
  const outcome = row ? nutritionOutcome(row.outcome) : null
  const summary = receipt.summary
  return (
    <section className="space-y-6" aria-label="Receipt outcome">
      {outcome && (
        <div
          className={cn(
            "flex items-start gap-2.5 rounded-xl px-3.5 py-3 text-sm",
            OUTCOME_SURFACES[outcome.tone]
          )}
        >
          {outcome.tone === "success" ? (
            <Check className="mt-0.5 size-4 shrink-0" />
          ) : (
            <CircleAlert className="mt-0.5 size-4 shrink-0" />
          )}

          <div>
            <p className="font-semibold">
              {outcome.label}

              {row?.imageElapsedMs !== null && row?.imageElapsedMs !== undefined
                ? ` · ${nutritionDuration(row.imageElapsedMs)}`
                : ""}
            </p>

            <p className="mt-0.5 text-foreground">{receiptOutcomeDescription(detail, row)}</p>
          </div>
        </div>
      )}

      {receipt.validation === "rejected" && (
        <p className="text-sm text-destructive">{`Rejected: ${workflowReason(receipt.validationReason)}. Correct the source event before sending it again.`}</p>
      )}

      {receipt.coverage !== "instrumented" && (
        <p className="text-sm text-warning">{workflowCoverage(receipt.coverage)}</p>
      )}

      {receipt.summary.unknown && (
        <p className="text-sm text-warning">
          Missing stage evidence. Completion cannot be confirmed.
        </p>
      )}

      {receipt.summary.historicalFailed && !receipt.summary.failed && (
        <p className="text-sm">
          Earlier failures remain in history. Their obligations are resolved; they are not new
          successes.
        </p>
      )}

      {children}

      <ol className="grid list-none p-0">
        {receiptTimeline(receipt, row).map((stage) => (
          <li
            key={stage.title}
            className={cn(
              "relative grid grid-cols-[20px_minmax(0,1fr)] gap-x-3 gap-y-0 pb-4 last:pb-0 last:before:hidden before:absolute before:bottom-1 before:left-[9px] before:top-6 before:w-0.5 before:content-['']",
              stage.complete ? "before:bg-success" : "before:bg-border"
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "inline-flex size-5 flex-none items-center justify-center rounded-full border-[1.5px]",
                TIMELINE_STYLES[timelineState(stage)]
              )}
            >
              <TimelineIcon state={timelineState(stage)} />
            </span>

            <div className="min-w-0">
              <div className="flex flex-wrap justify-between gap-x-3">
                <span className="text-sm font-medium">
                  {stage.title}

                  <span className="sr-only">{`: ${timelineState(stage)}`}</span>
                </span>

                <span className="text-xs tabular-nums text-muted-foreground">{stage.time}</span>
              </div>

              <p className="break-all text-xs text-muted-foreground">{stage.detail}</p>

              {stage.extra && <p className="text-xs text-muted-foreground">{stage.extra}</p>}
            </div>
          </li>
        ))}
      </ol>

      <p className="text-xs text-muted-foreground">
        Pub/Sub acceptance does not confirm Magento processing.
      </p>

      <details className="text-xs">
        <summary className="min-h-11 cursor-pointer content-center text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring">
          Receipt and delivery evidence
        </summary>

        <p>{`${receipt.transportAttempts} transport deliveries · ${receipt.skuCount} SKUs · ${summary.scopes} actual scopes`}</p>

        {receipt.deliveries?.map((delivery) => (
          <div key={delivery.id}>
            <p>{`Delivery #${delivery.id}: ${humanizeEnumLabel(delivery.state)} · ${delivery.processingAttempts} processing attempts`}</p>

            {delivery.nextRetryAt && <p>{`Next retry ${formatDateTime(delivery.nextRetryAt)}`}</p>}
          </div>
        ))}
      </details>
    </section>
  )
}
const OUTCOME_SURFACES = {
  success: "bg-success/10 text-success",
  destructive: "bg-destructive/10 text-destructive",
  warning: "bg-warning/10 text-warning",
  info: "bg-info/10 text-info",
  muted: "bg-muted text-muted-foreground",
  brand: "bg-brand/10 text-brand",
} as const
function receiptOutcomeDescription(
  detail: PimWorkflowDetail,
  row: NutritionQueueReceipt | undefined
) {
  const descriptions: Partial<Record<NutritionQueueReceipt["outcome"], string>> = {
    images_published: "Every applicable image is recorded and its linked publications accepted.",
    images_ready: "Images are recorded. Publication is still pending.",
    no_change: "Retained evidence confirms no new image was needed.",
    withdrawn: "An intentional image withdrawal was recorded.",
    unknown: "Retained evidence cannot establish completion.",
    superseded: "Newer work superseded this receipt's image obligation.",
    unsupported: "This scope has a recorded unsupported outcome.",
    paused: "A worker control is paused. Its obligations remain outstanding.",
    failed: "Inspect the failed scopes and recorded cause before recovery.",
    rejected: "The source event did not pass validation.",
  }
  return (
    (row && descriptions[row.outcome]) ??
    `${detail.receipt.summary.upload.complete} of ${detail.receipt.summary.scopes} scopes uploaded.`
  )
}
function receiptTimeline(
  receipt: PimWorkflowDetail["receipt"],
  row: NutritionQueueReceipt | undefined
) {
  const summary = receipt.summary
  return [
    {
      title: "PIM event received",
      complete: receipt.validation === "accepted",
      failed: receipt.validation === "rejected",
      pending: false,
      detail: `Pub/Sub message ${receipt.messageId ?? "unknown"}`,
      extra: `Receipt ${receipt.id} · ${humanizeEnumLabel(receipt.validation)}`,
      time: formatDateTime(receipt.receivedAt),
    },
    {
      title: "Store images queued",
      complete:
        summary.generation.total > 0 && summary.generation.complete === summary.generation.total,
      failed: summary.generation.failed > 0,
      pending: summary.generation.pending > 0,
      detail: `${summary.scopes} store/SKU scopes · ${summary.generation.complete} generated`,
    },
    {
      title: "Uploaded to GCS",
      complete: summary.upload.total > 0 && summary.upload.complete === summary.upload.total,
      failed: summary.upload.failed > 0,
      pending: summary.upload.pending > 0,
      detail: `${summary.upload.complete} of ${summary.upload.total} scopes uploaded`,
      time: row?.imageCompleteAt ? formatDateTime(row.imageCompleteAt) : undefined,
    },
    {
      title: "Made current / withdrawn",
      complete: summary.current.total > 0 && summary.current.complete === summary.current.total,
      failed: summary.current.failed > 0,
      pending: summary.current.pending > 0,
      resolution: currentResolution(summary.current),
      detail: `${summary.current.complete} of ${summary.current.total} Current updates complete · ${summary.current.failed} failed · ${summary.current.pending} pending · ${summary.current.unknown} unknown · ${summary.current.notApplicable} not required · ${summary.current.superseded} superseded`,
      extra:
        "A completed Current update records image selection or withdrawal. It does not confirm current object availability.",
    },
    {
      title: "Published to Pub/Sub",
      complete:
        summary.publication.total > 0 && summary.publication.complete === summary.publication.total,
      failed: summary.publication.failed > 0,
      pending: summary.publication.pending > 0,
      detail: `${summary.publication.complete} of ${summary.publication.total} scopes published`,
    },
  ]
}
const CURRENT_RESOLUTION = {
  notRequired: "not required",
  superseded: "superseded",
  resolved: "resolved",
} as const
type CurrentResolution = (typeof CURRENT_RESOLUTION)[keyof typeof CURRENT_RESOLUTION]
function currentResolution(counts: PimWorkflowDetail["receipt"]["summary"]["current"]) {
  if (counts.total === 0) {
    return undefined
  }
  if (counts.notApplicable === counts.total) {
    return CURRENT_RESOLUTION.notRequired
  }
  if (counts.superseded === counts.total) {
    return CURRENT_RESOLUTION.superseded
  }
  if (
    counts.complete < counts.total &&
    counts.complete + counts.notApplicable + counts.superseded === counts.total
  ) {
    return CURRENT_RESOLUTION.resolved
  }
  return undefined
}
const TIMELINE_STYLES = {
  complete: "border-success bg-success text-success-foreground",
  failed: "border-destructive bg-destructive text-destructive-foreground",
  pending: "border-info text-info",
  unknown: "border-border text-muted-foreground",
  [CURRENT_RESOLUTION.notRequired]: "border-border text-muted-foreground",
  [CURRENT_RESOLUTION.superseded]: "border-border text-muted-foreground",
  [CURRENT_RESOLUTION.resolved]: "border-border text-muted-foreground",
} as const
function timelineState(stage: {
  complete: boolean
  failed: boolean
  pending: boolean
  resolution?: CurrentResolution | undefined
}): keyof typeof TIMELINE_STYLES {
  if (stage.failed) {
    return "failed"
  }
  if (stage.pending) {
    return "pending"
  }
  if (stage.complete) {
    return "complete"
  }
  return stage.resolution ?? "unknown"
}
function TimelineIcon({ state }: { state: keyof typeof TIMELINE_STYLES }) {
  if (state === "complete") {
    return <Check className="size-3" />
  }
  if (state === "failed") {
    return <X className="size-3" />
  }
  if (state === "pending") {
    return <span className="size-2 rounded-full bg-info" />
  }
  return null
}

function NutritionScope({
  scope,
  initiallyOpen,
  recorded,
}: {
  scope: PimWorkflowScope
  initiallyOpen: boolean
  recorded: PimWorkflowDetail["attempts"]
}) {
  const ref = useRef<HTMLDetailsElement>(null)
  useEffect(() => {
    if (initiallyOpen) {
      ref.current?.setAttribute("open", "")
      ref.current?.querySelector("summary")?.focus()
      ref.current?.scrollIntoView?.({ block: "nearest" })
    }
  }, [initiallyOpen])
  return (
    <details
      id={`nutrition-scope-${scope.id}`}
      ref={ref}
      className="not-first:border-t not-first:border-border"
    >
      <summary className="grid min-h-14 cursor-pointer list-none grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-3 px-3 py-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring [&::-webkit-details-marker]:hidden max-sm:grid-cols-[40px_minmax(0,1fr)]">
        <NutritionImage
          url={scopeImageUrl(scope, recorded)}
          label={`Nutrition image for ${scope.store}`}
          compact
          className="aspect-square size-10 rounded-md border border-border bg-white object-contain"
        />

        <span className="min-w-0">
          <span className="block break-words text-[13px] font-medium">{`${scope.store} · ${scope.sku ?? "SKU unresolved"}`}</span>

          <span className="block text-xs text-muted-foreground">
            {scope.current.recordedAt
              ? formatDateTime(scope.current.recordedAt)
              : humanizeEnumLabel(scope.generation.state)}
          </span>
        </span>

        <span className="flex flex-wrap justify-end gap-1 max-sm:col-start-2 max-sm:justify-start">
          <StatusBadge size="sm" tone={stageTone(scope.current.state)}>
            Image {humanizeEnumLabel(scope.current.state)}
          </StatusBadge>

          <StatusBadge size="sm" tone={stageTone(scope.publication.state)}>
            Pub/Sub {humanizeEnumLabel(scope.publication.state)}
          </StatusBadge>
        </span>
      </summary>

      <div className="space-y-4 px-3 pb-4 pt-1">
        <p className="break-words text-xs text-muted-foreground">{`Frozen target #${scope.targetId} · ${scope.frozenStore} · shared by ${scope.sharedReceiptCount} receipts`}</p>

        {scope.recovery?.resolvedAt && (
          <p className="text-xs">{`Target recovery verified ${formatDateTime(scope.recovery.resolvedAt)}. Failed stages below remain historical evidence.`}</p>
        )}

        {WORKFLOW_STAGES.some(([stage]) => scope[stage].state === "retrying") && (
          <p className="text-xs">
            A retry is observed in retained work. This is separate from manual recovery eligibility.
          </p>
        )}

        {scopeFailures(scope).length > 0 && (
          <p className="text-xs">
            Inspect the recorded cause. Use the exact target preview below to check manual recovery
            eligibility.
          </p>
        )}

        <div className="grid grid-cols-[144px_minmax(0,1fr)] gap-4 max-sm:grid-cols-1">
          <NutritionImage
            url={scopeImageUrl(scope, recorded)}
            label={`Recorded nutrition image ${scope.store} ${scope.sku}`}
            className="aspect-square w-full max-w-60 rounded-lg border border-border bg-white object-contain sm:w-36"
          />

          <div className="grid grid-cols-2 gap-4">
            {(
              [
                ["Dispatch", scope.dispatch],
                ["Content", scope.content],
                ["Generation", scope.generation],
                ["Upload", scope.upload],
                ["Recorded current image", scope.current],
                ["Pub/Sub acceptance", scope.publication],
              ] as const
            ).map(([label, stage]) => (
              <div key={label}>
                <p className="mb-2 text-xs font-medium">{label}</p>

                <WorkflowStage stage={stage} />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1 break-all text-xs text-muted-foreground">
          <p>{`Fill job: ${scope.fillJobId ?? "Unknown"}`}</p>

          <p>{`Replacement: ${scope.replacementId ?? "None recorded"}`}</p>

          <p>{`Render observation: ${scope.renderObservationId ?? "None recorded"}`}</p>
        </div>

        <ScopeImage scope={scope} recorded={recorded} />

        <p className="text-xs text-muted-foreground">
          Every linked publication is listed in recorded evidence below. Load further evidence pages
          when needed.
        </p>
      </div>
    </details>
  )
}

function NutritionAttempt({ attempt }: { attempt: PimWorkflowDetail["attempts"][number] }) {
  const url =
    typeof attempt.evidence.url === "string" && /^https?:\/\//.test(attempt.evidence.url)
      ? attempt.evidence.url
      : null
  return (
    <details className="border-b border-border pb-2">
      <summary className="min-h-11 cursor-pointer py-3 text-xs focus-visible:ring-2 focus-visible:ring-ring">
        {`${humanizeEnumLabel(attempt.category)} · ${attempt.store ?? "Unknown store"} · ${attempt.sku ?? "Unknown SKU"} · ${humanizeEnumLabel(attempt.state)}`}

        <time className="mt-1 block text-muted-foreground" dateTime={attempt.recordedAt}>
          {formatDateTime(attempt.recordedAt)}
        </time>
      </summary>

      <div className="space-y-3 pb-3">
        {attempt.reason && (
          <p className="text-xs">{`Recorded cause: ${workflowReason(attempt.reason)}`}</p>
        )}

        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="block max-w-60 rounded-lg focus-visible:ring-2 focus-visible:ring-ring"
          >
            <NutritionImage
              url={url}
              label={`Recorded nutrition image ${attempt.store} ${attempt.sku}`}
              className="aspect-square w-full rounded-lg border border-border bg-white object-contain"
            />

            <span className="flex min-h-11 items-center text-xs text-info underline">
              Open recorded image
            </span>
          </a>
        )}

        <dl className="space-y-2 text-xs">
          {Object.entries(attempt.evidence).map(([key, value]) => (
            <div key={key}>
              <dt className="text-muted-foreground">{humanizeEnumLabel(key)}</dt>

              <dd className="break-all">{value === null ? "Unknown" : String(value)}</dd>
            </div>
          ))}
        </dl>

        <p className="break-all text-xs text-muted-foreground">{`Evidence ID: ${attempt.id} · Parent: ${attempt.parentId ?? "None"}`}</p>
      </div>
    </details>
  )
}
function CopyReceipt({ receiptId }: { receiptId: number }) {
  const [status, setStatus] = useState("")
  return (
    <div>
      <Button
        className="min-h-11"
        onClick={() => {
          void navigator.clipboard.writeText(String(receiptId)).then(
            () => setStatus("Copied"),
            () => setStatus("Copy unavailable")
          )
        }}
      >
        Copy receipt ID
      </Button>

      <span role="status" className="ml-2 text-xs">
        {status}
      </span>
    </div>
  )
}

function NutritionScopeList({
  detail,
  scopes,
  total,
  selectedStore,
  selectedSku,
  selectedScope,
  failedOnly,
  onScopeFilter,
  recorded,
}: {
  detail: ReturnType<typeof useNutritionQueue>["detail"]
  scopes: PimWorkflowScope[]
  total: number
  selectedStore: string | undefined
  selectedSku: string | undefined
  selectedScope: string | undefined
  failedOnly: boolean
  onScopeFilter: (failed: boolean) => void
  recorded: PimWorkflowDetail["attempts"]
}) {
  return (
    <section className="space-y-3" aria-label="Store and SKU scopes">
      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-semibold">{`Store / SKU scopes · ${total}`}</h3>

        <NutritionHelp label="About store scopes">{`${scopes.length} loaded scopes. Frozen targets can fan out across stores and SKUs. Stage counts include all receipt scopes. Missing target pages must be inspected when failures remain undisplayed.`}</NutritionHelp>
      </div>

      <div className="flex flex-wrap gap-2" aria-label="Scope filter">
        <Button
          className="min-h-11"
          aria-pressed={!failedOnly}
          onClick={() => onScopeFilter(false)}
        >
          All scopes
        </Button>

        <Button className="min-h-11" aria-pressed={failedOnly} onClick={() => onScopeFilter(true)}>
          Failed scopes
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        {failedOnly
          ? "Showing unresolved failed scopes. Receipt totals still include every scope."
          : "Showing all scopes. Receipt totals include unloaded pages."}
      </p>

      <div className="overflow-hidden rounded-xl border border-border">
        {scopes.map((scope) => (
          <NutritionScope
            key={scope.id}
            scope={scope}
            initiallyOpen={
              selectedScope
                ? scope.id === selectedScope
                : scope.store === selectedStore && (!selectedSku || scope.sku === selectedSku)
            }
            recorded={recorded}
          />
        ))}
      </div>

      {detail.isPlaceholderData && (
        <p role="status" className="text-xs">
          Reading filtered scopes…
        </p>
      )}

      {scopes.length === 0 && !detail.isPlaceholderData && (
        <p className="text-sm text-warning">
          {failedOnly
            ? "No unresolved failed scopes match this page. Other receipt evidence remains above."
            : "No retained scope evidence. Missing scopes cannot establish completion."}
        </p>
      )}

      {!detail.isPlaceholderData &&
        (detail.hasNextPage || (detail.data?.pages.length ?? 0) > 1) && (
          <Button
            className="min-h-11 aria-disabled:opacity-50"
            aria-disabled={!detail.hasNextPage || detail.isFetchingNextPage}
            aria-busy={detail.isFetchingNextPage}
            onClick={() => {
              if (detail.hasNextPage && !detail.isFetchingNextPage) {
                void detail.fetchNextPage()
              }
            }}
          >
            {detail.isFetchingNextPage
              ? "Loading scopes…"
              : moreScopesLabel(failedOnly, detail.hasNextPage)}
          </Button>
        )}
    </section>
  )
}
function NutritionAttempts({
  attempts,
  recorded,
}: {
  attempts: ReturnType<typeof useNutritionQueue>["attempts"]
  recorded: PimWorkflowDetail["attempts"]
}) {
  return (
    <section className="space-y-3" aria-label="Recorded attempts and publications">
      {attempts.error && (
        <p role="alert" className="text-sm text-destructive">
          {attempts.data ? "Displayed attempts are stale. " : "Attempts unavailable. "}

          {attempts.error.message}
        </p>
      )}

      <details>
        <summary className="min-h-11 cursor-pointer content-center text-[13px] font-semibold focus-visible:ring-2 focus-visible:ring-ring">
          Recorded attempts and publication obligations
        </summary>

        <p className="text-xs text-muted-foreground">
          Delivery processing, fill, render, replacement, and publisher attempts are separate.
          Published means accepted by Pub/Sub for the intended Magento consumer. Magento processing
          is unconfirmed.
        </p>

        {recorded.map((attempt) => (
          <NutritionAttempt key={attempt.id} attempt={attempt} />
        ))}

        {attempts.hasNextPage && (
          <Button
            className="min-h-11"
            disabled={attempts.isFetchingNextPage}
            onClick={() => {
              void attempts.fetchNextPage()
            }}
          >
            Show more recorded evidence
          </Button>
        )}
      </details>
    </section>
  )
}

function NutritionDetailHeading({
  receiptId,
  row,
  first,
  onClose,
}: Pick<Props, "receiptId" | "row" | "onClose"> & { first: PimWorkflowDetail | undefined }) {
  const sku = row?.skus[0] ?? first?.receipt.sku ?? "SKU unresolved"
  const remaining = Math.max(0, (row?.skus.length ?? 1) - 1)
  const skuLabel = remaining > 0 ? `${sku} + ${remaining} more` : sku
  return (
    <header className="flex items-start justify-between gap-3 border-b border-border p-5">
      <div className="min-w-0">
        <h2 className="break-words text-lg font-semibold">
          {row?.productName ?? `Receipt #${receiptId}`}
        </h2>

        <p className="mt-1 break-words text-xs text-muted-foreground">{`${skuLabel} · Product ${first?.receipt.productId ?? "unresolved"}`}</p>
      </div>

      <Button
        variant="ghost"
        className="min-h-11 min-w-11 shrink-0 px-2"
        onClick={onClose}
        aria-label="Close receipt"
      >
        <X className="size-5" />
      </Button>
    </header>
  )
}

function receiptScopes(queue: ReturnType<typeof useNutritionQueue>) {
  if (queue.detail.isPlaceholderData) {
    return []
  }
  const targets = [
    ...(queue.selection.data?.targets ?? []),
    ...(queue.detail.data?.pages.flatMap((page) => page.targets) ?? []),
  ]
  return [...new Map(targets.map((scope) => [scope.id, scope])).values()]
}
function receiptAttempts(queue: ReturnType<typeof useNutritionQueue>) {
  const attempts = [
    ...(queue.selection.data?.attempts ?? []),
    ...(queue.attempts.data?.pages.flatMap((page) => page.attempts) ?? []),
  ]
  return [...new Map(attempts.map((attempt) => [attempt.id, attempt])).values()]
}
function ScopeImage({
  scope,
  recorded,
}: {
  scope: PimWorkflowScope
  recorded: PimWorkflowDetail["attempts"]
}) {
  const observation = recorded.find(
    (attempt) => attempt.id === `render:${scope.renderObservationId}`
  )
  if (!observation) {
    return (
      <p className="text-xs text-muted-foreground">
        Image evidence is not loaded. Open the remaining recorded evidence pages below.
      </p>
    )
  }
  return <NutritionAttempt attempt={observation} />
}

function scopeImageUrl(scope: PimWorkflowScope, recorded: PimWorkflowDetail["attempts"]) {
  const observation = recorded.find(
    (attempt) => attempt.id === `render:${scope.renderObservationId}`
  )
  const url = observation?.evidence.url
  return typeof url === "string" && /^https?:\/\//.test(url) ? url : null
}
function stageTone(state: string) {
  if (state === "complete") {
    return "success"
  }
  if (state === "failed") {
    return "destructive"
  }
  if (state === "pending") {
    return "info"
  }
  if (state === "blocked" || state === "unknown") {
    return "warning"
  }
  return "muted"
}

function moreScopesLabel(failedOnly: boolean, hasNextPage: boolean) {
  if (!hasNextPage) {
    return failedOnly ? "All failed scopes loaded" : "All scopes loaded"
  }
  return failedOnly ? "Show more failed scopes" : "Show more scopes"
}
